// components/ChangePassword.js
import React, { useState, useContext } from "react";

import { useNavigate } from "react-router-dom";

import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import { useChangePasswordMutation } from "../../services/auth";
import { AuthContext } from "../../context/AuthContext";
import { NotificationContext } from "../../context/NotificationContext";


function ChangePassword() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { addNotification } = useContext(NotificationContext);
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [currentPasswordError, setCurrentPasswordError] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    let valid = true;
    if (!user.forcechangepw && !currentPassword) {
      setCurrentPasswordError(true);
      addNotification("Current Password is required.", "error");
      valid = false;
    } else {
      setCurrentPasswordError(false);
    }

    if (!newPassword) {
      setNewPasswordError(true);
      addNotification("New Password is required.", "error");
      valid = false;
    } else {
      setNewPasswordError(false);
    }

    if (newPassword !== confirmPassword) {
      setConfirmPasswordError(true);
      addNotification("Passwords do not match.", "error");
      valid = false;
    } else {
      setConfirmPasswordError(false);
    }

    if (!valid) return;

    try {
      await changePassword({
        userId: user.id,
        currentPassword: user.forcechangepw ? null : currentPassword,
        newPassword,
      }).unwrap();
      // console.log("Change Password result:", result);

      addNotification("Password updated successfully.", "success");

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      if (user.forcechangepw) {
        setTimeout(() => {
          navigate("/");
        }, 1500);
      }
    } catch (error) {
      console.error("Change password error:", error);
      const errorMessage =
        error?.data?.message || "An unexpected error occurred.";
      addNotification(`Error: ${errorMessage}`, "error");
    }
  };

  const handleClickShowCurrentPassword = () => {
    setShowCurrentPassword((prev) => !prev);
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword((prev) => !prev);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);

    if (confirmPassword && value !== confirmPassword) {
      setConfirmPasswordError(true);
    } else {
      setConfirmPasswordError(false);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);

    if (newPassword && value !== newPassword) {
      setConfirmPasswordError(true);
    } else {
      setConfirmPasswordError(false);
    }
  };

  return (
    <div className="change-password-container">
      <Typography variant="h5">Change Your Password</Typography>
      <form onSubmit={handleSubmit}>
        {!user.forcechangepw && (
          <TextField
            label="Current Password"
            type={showCurrentPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            error={currentPasswordError}
            helperText={
              currentPasswordError ? "Current password is required." : ""
            }
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowCurrentPassword}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
        <TextField
          label="New Password"
          type={showNewPassword ? "text" : "password"}
          variant="outlined"
          fullWidth
          required
          value={newPassword}
          onChange={handleNewPasswordChange}
          error={newPasswordError}
          helperText={newPasswordError ? "New password is required." : ""}
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowNewPassword} edge="end">
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Confirm New Password"
          type={showConfirmPassword ? "text" : "password"}
          variant="outlined"
          fullWidth
          required
          value={confirmPassword}
          onChange={handleConfirmPasswordChange}
          error={confirmPasswordError}
          helperText={confirmPasswordError ? "Passwords do not match." : ""}
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowConfirmPassword} edge="end">
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={isLoading || newPassword !== confirmPassword}
          startIcon={isLoading && <CircularProgress size={20} />}
          sx={{ marginTop: "1rem" }}
        >
          {isLoading ? "Updating..." : "Update Password"}
        </Button>
      </form>
    </div>
  );
}

export default ChangePassword;
